import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { api } from '@shared/api/client';
import { key } from '@shared/api/query-keys';

import type { CategoryNode } from '../_shared/types';

/**
 * The three writes the catalogue grants this surface: create, rename or
 * re-parent, and a delete confirmed by name.
 *
 * **No reorder.** The owning service publishes `POST /admin/categories:reorder`
 * and this surface does not call it — `[x]` §7.1 gives the tree depth and says
 * nothing about order among siblings, so move handles here would promise a rank
 * the catalogue does not give the entity. That is the difference from the shelf
 * table, whose row calls the list «ordered» in as many words.
 *
 * **Mutations never auto-retry.** A repeat of a write whose outcome is unknown
 * is the operator's decision, not the client's. Retry safety on this service
 * comes from the shape of the writes instead: the unique index on the live slug
 * refuses a duplicate rather than creating a second one, and `confirm=true` is
 * required on the delete.
 *
 * **`Idempotency-Key` is not sent.** The header is scoped platform-wide to
 * finance-bound money-moving writes, and `backend-content` accepts it on no
 * endpoint; sending it would document a contract that does not exist.
 */

/**
 * Both vocabularies, on every write.
 *
 * A category write cannot move a tag row — but the two queries are one
 * invalidation set in the platform's own map, and the reason is the surfaces
 * downstream: a shelf's source parameter, an editorial post's tag field, and the
 * authoring form's taxonomy block all read whichever of the two they need from
 * the same cache, and a console that refreshed only half of it would leave one
 * of them holding a vocabulary the operator has just edited.
 */
function invalidateVocabularies(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: key('content', 'categories') });
  void queryClient.invalidateQueries({ queryKey: key('content', 'tags') });
}

export interface CategoryWrite {
  name: string;
  /** `null` is the top level, and it is sent rather than omitted: promoting a
   *  node to a root is a real edit, and `exclude_unset` on the service side
   *  would drop an omitted key as «unchanged». */
  parentId: string | null;
}

export function useCreateCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CategoryWrite) => api.post<CategoryNode>('content', '/admin/categories', input),
    onSuccess: () => invalidateVocabularies(queryClient),
  });
}

export function useUpdateCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ categoryId, ...input }: CategoryWrite & { categoryId: string }) =>
      api.patch<CategoryNode>('content', `/admin/categories/${categoryId}`, input),
    onSuccess: () => invalidateVocabularies(queryClient),
  });
}

/**
 * `confirm=true` is the wire half of the dialog, and this hook is called from a
 * `<ConfirmDialog>` accept handler and from nowhere else. A code path that sets
 * the flag from anywhere else has removed the guard while leaving the dialog on
 * screen.
 */
export function useDeleteCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (categoryId: string) =>
      api.delete<null>('content', `/admin/categories/${categoryId}?confirm=true`),
    onSuccess: () => invalidateVocabularies(queryClient),
  });
}
